import React, { useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import ContactOverlay from './contactOverlay.jsx'
import OverlayNavigation from './overlayNavigation.jsx'
import './unifiedUI.scss'

/**
 * ContactGallery Component
 * Single-level contact display following aboutGallery pattern
 */
const ContactGallery = ({ state, onRequestClose }) => {
  const [animationState, setAnimationState] = useState('closed') // 'closed' | 'opening' | 'open' | 'closing'
  
  // Handle state changes from parent - 4-state machine
  useEffect(() => {
    if (state === 'open' && animationState === 'closed') {
      setAnimationState('opening')
      const timer = setTimeout(() => {
        setAnimationState('open')
      }, 500)
      return () => clearTimeout(timer)
    } else if (state === 'closed' && animationState === 'open') {
      setAnimationState('closing')
      const timer = setTimeout(() => {
        setAnimationState('closed')
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [state])

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key !== 'Escape') return
      if (animationState !== 'open') return
      
      event.preventDefault()
      event.stopPropagation()
      
      console.log('ESC: Closing contact overlay')
      onRequestClose()
    }

    window.addEventListener('keydown', handleEscKey, true)
    return () => window.removeEventListener('keydown', handleEscKey, true)
  }, [animationState, onRequestClose])

  // Don't render if closed
  if (animationState === 'closed') return null

  // Determine CSS class based on animation state
  const getClassName = () => {
    if (animationState === 'opening') return 'canvas-work-gallery opening'
    if (animationState === 'closing') return 'canvas-work-gallery closing'
    return 'canvas-work-gallery open'
  }

  return (
    <div className={getClassName()}>
      <Canvas
        linear
        flat
        camera={{
          fov: 50,
          near: 0.1,
          far: 1000,
          position: [0, 0, 15]
        }}
        dpr={[1, 2]}
        style={{ 
          width: '100vw', 
          height: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          background: 'linear-gradient(180deg, #5d728eff 0%, #687d99ff 100%)',
          backgroundSize: '100% 100%'
        }}
      >
        <ContactOverlay />
      </Canvas>
      
      {/* Noise overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        opacity: 0.4,
        mixBlendMode: 'overlay',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px'
      }} />

      {/* Navigation UI overlay */}
      <div className="canvas-ui-overlay">
        <OverlayNavigation
          breadcrumbPath={[{ label: 'CONTACT', level: 1 }]}
          onClose={onRequestClose}
        />
      </div>
    </div>
  )
}

export default ContactGallery
